import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/lib/sentry";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PlanProvider } from "@/contexts/PlanContext";
import { TrialBanner } from "@/components/TrialBanner";
import Login from "./pages/Login";
import DashboardOfertyPage from "./pages/dashboard/DashboardOfertyPage";
import DashboardStartPage from "./pages/dashboard/DashboardStartPage";
import ProjectDetails from "./pages/ProjectDetails";
import SettingsPage from "./pages/SettingsPage";
import AnalyzeEstimatePage from "./pages/AnalyzeEstimatePage";
import NotFound from "./pages/NotFound";
import MvpEstimatePage, { MvpWizardShellLayout } from "./pages/MvpEstimatePage";
import MvpNewDraftRedirect from "./pages/MvpNewDraftRedirect";
import MvpRootRedirect from "./pages/MvpRootRedirect";
import OnboardingPage from "./pages/OnboardingPage";
import PricingPage from "./pages/PricingPage";
import PublicOfferPage from "./pages/PublicOfferPage";
import MojeStawki from "./pages/MojeStawki";
import { shouldShowOnboarding } from "@/data/companyPrefs";
import { ReactNode } from "react";

const queryClient = new QueryClient();

function LayoutErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <p className="text-muted-foreground text-center">
        Wystąpił nieoczekiwany błąd. Odśwież stronę lub spróbuj ponownie później.
      </p>
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Ładowanie...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (shouldShowOnboarding() && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

/** Login z obsługą `location.state.returnTo` po zalogowaniu (np. powrót do MVP). */
function LoginRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const safeReturn =
    typeof returnTo === "string" && returnTo.startsWith("/") && !returnTo.startsWith("//")
      ? returnTo
      : null;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">Ładowanie...</div>
    );
  }
  if (user) {
    return <Navigate to={safeReturn ?? "/"} replace />;
  }
  return <Login />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary fallback={<LayoutErrorFallback />} showDialog={false}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <PlanProvider>
              <TrialBanner />
              <Routes>
                <Route
                  path="/mvp"
                  element={
                    <ProtectedRoute>
                      <MvpWizardShellLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<MvpRootRedirect />} />
                  <Route path="new" element={<MvpNewDraftRedirect />} />
                  <Route path=":draftId" element={<MvpEstimatePage />} />
                </Route>
                <Route path="/oferta/:token" element={<PublicOfferPage />} />
                <Route path="/login" element={<LoginRoute />} />
                <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
                <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><DashboardStartPage /></ProtectedRoute>} />
                <Route path="/dashboard/oferty" element={<ProtectedRoute><DashboardOfertyPage /></ProtectedRoute>} />
                <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
                <Route path="/nowa-oferta" element={<Navigate to="/mvp/new" replace />} />
                <Route path="/analiza-kosztorysu" element={<ProtectedRoute><AnalyzeEstimatePage /></ProtectedRoute>} />
                <Route path="/projekt/:id" element={<ProtectedRoute><ProjectDetails /></ProtectedRoute>} />
                <Route path="/ustawienia" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route path="/moje-stawki" element={<ProtectedRoute><MojeStawki /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PlanProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ErrorBoundary>
  </QueryClientProvider>
);

export default App;
